import { useEffect, useRef, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router";
// import Signin from "../../Auth/SignIn";
// import SignUp from "../../Auth/SignUp";
import { useTheme } from "next-themes";
import { Logo } from "../Logo";
import LanguageDetector from "./component/LanguageDetector";
import { headerData } from "./data/headerData";
import HeaderLink from "./component/navigation/HeaderLink";
import SearchModal from "./component/SearchModal";
import { HeaderItem } from "./types/menu.type";
import MobileHeaderLink from "./component/navigation/MobileHeaderLink";
import DefaultNavigation from "./component/navigation/DefualtNavigation";
import useMenu from "@/hooks/use-menu";
import { useAuthStore } from "../../auth/auth.store";


const Header: React.FC = () => {
  const { theme, setTheme } = useTheme();

  const showMenu = useMenu((state) => state.showMenu);
  const pages = useMenu((state) => state.links);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [notifications] = useState<number>(0);
  const [messenger] = useState<number>(0);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [isSignInOpen, setIsSignInOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);

  const signInRef = useRef<HTMLDivElement>(null);
  const signUpRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleScroll = useCallback(() => {
    setSticky(window.scrollY >= 80);
  }, []);

  const handleNavbarClose = () => {
    setNavbarOpen(false);
    setOpenIndex(null)
  }

  const isAuthenticated = useAuthStore(
    (state) => state.isAuthenticated
  );  
  

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;

    if (signInRef.current && !signInRef.current.contains(target)) {
      setIsSignInOpen(false);
    }

    if (signUpRef.current && !signUpRef.current.contains(target)) {
      setIsSignUpOpen(false);
    }

    if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
      setNavbarOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleScroll, handleClickOutside]);

  useEffect(() => {
    if (isSignInOpen || isSignUpOpen || navbarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isSignInOpen, isSignUpOpen, navbarOpen]);

  return (
    <>
      <header
        className={`fixed top-0 z-40 w-full pb-5 transition-all duration-300 ${sticky
          ? "shadow-lg bg-white dark:bg-deepSlate text-midnight_text pt-3"
          : "shadow-none md:pt-5 pt-3"
          }`}
      >
        <div className="lg:py-0 py-2">
          <div className="container mx-auto lg:max-w-screen-2xl md:max-w-screen-md">
            {!navbarOpen && (
              <div className="hidden lg:flex justify-between items-center mb-2 space-x-4">
                <Logo className="h-16" />

                <div className="flex items-center space-x-4">
                  {/* Search button */}
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 text-slate-300 dark:hover:text-white dark:text-white transition-all group"
                    aria-label="Open search"
                  >
                    <svg
                      className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.2}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                    </svg>
                    <span className="hidden sm:inline text-xs">Search</span>

                  </button>

                  <Link to="#" className="dark:text-white">Prtenership</Link>

                  {
                  isAuthenticated ? <>
                    <Link to="/profile">
                      <Icon
                        icon="lucide:user"
                        className="dark:text-white"
                        width="24"
                        height="24"
                      />
                    </Link>
                  </> : <>
                    <Link
                      to="/login"
                      className="dark:text-white"
                    >
                      Login
                    </Link>
                  </>
                  }

                  <Link
                    to="/profile/notification"
                    className="relative dark:text-white hover:text-red-400"
                    aria-label="Notifications"
                  >
                    <Icon icon="fa-solid:bell" width={20} />
                    {notifications > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                        {notifications}
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/messenger"
                    className="relative hover:text-red-400"
                    aria-label="messenger"
                  >
                    <img src="/images/commuinty/messenger.svg" alt="messenger olx logo" width={24} />

                    {messenger > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                        {messenger}
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/p2p"
                    className="relative dark:text-white hover:text-red-400"
                    aria-label="P2P"
                  >
                    <Icon icon="ri:p2p-line" width={20} />
                  </Link>

                  <LanguageDetector />

                  <button
                    aria-label="Toggle theme"
                    onClick={() =>
                      setTheme(theme === "dark" ? "light" : "dark")
                    }
                    className="flex h-8 w-8 items-center justify-center cursor-pointer dark:text-white"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      className="hidden h-6 w-6 dark:block"
                    >
                      <path
                        fill="#FFFFFF"
                        d="M4.50663 3.2267L3.30663 2.03337L2.36663 2.97337L3.55996 4.1667L4.50663 3.2267ZM2.66663 7.00003H0.666626V8.33337H2.66663V7.00003ZM8.66663 0.366699H7.33329V2.33337H8.66663V0.366699ZM13.6333 2.97337L12.6933 2.03337L11.5 3.2267L12.44 4.1667L13.6333 2.97337ZM11.4933 12.1067L12.6866 13.3067L13.6266 12.3667L12.4266 11.1734L11.4933 12.1067ZM13.3333 7.00003V8.33337H15.3333V7.00003H13.3333ZM7.99996 3.6667C5.79329 3.6667 3.99996 5.46003 3.99996 7.6667C3.99996 9.87337 5.79329 11.6667 7.99996 11.6667C10.2066 11.6667 12 9.87337 12 7.6667C12 5.46003 10.2066 3.6667 7.99996 3.6667ZM7.33329 14.9667H8.66663V13H7.33329V14.9667ZM2.36663 12.36L3.30663 13.3L4.49996 12.1L3.55996 11.16L2.36663 12.36Z"
                      />
                    </svg>

                    <svg
                      viewBox="0 0 23 23"
                      className="h-8 w-8 text-dark dark:hidden"
                    >
                      <path d="M16.6111 15.855C17.591 15.1394 18.3151 14.1979 18.7723 13.1623C16.4824 13.4065 14.1342 12.4631 12.6795 10.4711C11.2248 8.47905 11.0409 5.95516 11.9705 3.84818C10.8449 3.9685 9.72768 4.37162 8.74781 5.08719C5.7759 7.25747 5.12529 11.4308 7.29558 14.4028C9.46586 17.3747 13.6392 18.0253 16.6111 15.855Z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="container mx-auto lg:max-w-screen-2xl md:max-w-3xl relative flex items-center justify-between px-4">
            <div className="hidden lg:flex flex-col justify-center items-center mx-auto">
              <nav className="hidden lg:flex grow items-center gap-8 justify-center">
                {headerData.map((item, index) => (
                  <HeaderLink key={index} item={item} />
                ))}
              </nav>
              {
                showMenu && <div
                  className={`hidden lg:block p-2 ${sticky ? "fixed top-10/100 sm:top-18/100 left-0 right-0 z-40 shadow-sm" : ""}`}
                >
                  <DefaultNavigation title="" menu={pages} />
                </div>
              }

            </div>

            <div className="flex justify-between lg:hidden items-center w-full">

              <div className="flex">
                <button
                  onClick={() => setNavbarOpen((prev) => !prev)}
                  className="block p-2 rounded-lg"
                  aria-label="Toggle mobile menu"
                >
                  <span className="block w-6 h-0.5 dark:bg-white bg-darkmode"></span>
                  <span className="block w-6 h-0.5 dark:bg-white bg-darkmode mt-1.5"></span>
                  <span className="block w-6 h-0.5 dark:bg-white bg-darkmode mt-1.5"></span>
                </button>

                {/* Search button */}
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="flex p-2 rounded-lg items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 text-slate-300 hover:text-white transition-all group"
                  aria-label="Open search"
                >
                  <svg
                    className="w-4 h-4 dark:text-white group-hover:scale-110 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.2}
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                  </svg>
                  <span className="hidden sm:inline text-xs">Search</span>
                </button>

              </div>
              <div className="flex justify-center ">
                <Logo className="h-20" />

              </div>
              <div className="flex justify-end items-center gap-x-2">






                {/* <Link
                  to="/profile/notification"
                  className="relative text-white ms-2 hover:text-red-400"
                  aria-label="Notifications"
                >
                  <Icon icon="fa-solid:bell" width={24} />
                  {notifications > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                      {notifications}
                    </span>
                  )}
                </Link> */}

                <Link
                  to="/messenger"
                  className="relative text-white ms-2 hover:text-red-400"
                  aria-label="messenger"
                >
                  <img src="/images/commuinty/messenger.svg" alt="messenger olx logo" width={24} />
                  {messenger > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                      {messenger}
                    </span>
                  )}
                </Link>

                <Link
                  to="/p2p"
                  className="relative dark:text-white"
                  aria-label="P2P"
                >
                  <Icon icon="ri:p2p-line" width={20} />
                </Link>

                <div className="lg:hidden">
                  {showMenu && <DefaultNavigation title="" menu={pages} />}
                </div>


              </div>



            </div>
          </div>

          {navbarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-70"
              onClick={() => setNavbarOpen(false)}
            />
          )}

          <div
            ref={mobileMenuRef}
            className={`lg:hidden fixed top-0 right-0 h-full w-full sm:w-[22rem] 
          bg-muted dark:bg-darkmode shadow-lg 
          transform transition-transform duration-300
          ${navbarOpen ? "translate-x-0 overflow-y-auto" : "translate-x-full"
              } z-70`}
          >
            <div className="flex items-center justify-between p-4">
              <Logo className="h-16"/>
              <button
                onClick={handleNavbarClose}
                className="bg-[url('/images/closed.svg')] bg-no-repeat bg-contain w-5 h-5"
              />
            </div>

            <div className="flex items-center flex-wrap space-x-4 px-3">

              {
                  isAuthenticated ? <>
                    <Link to="/profile">
                      <Icon
                        icon="lucide:user"
                        className="dark:text-white"
                        width="24"
                        height="24"
                      />
                    </Link>
                  </> : <>
                    <Link
                      to="/login"
                      className="dark:text-white"
                    >
                      Login
                    </Link>
                  </>
                  }



              <LanguageDetector />

              <button
                aria-label="Toggle theme"
                onClick={() =>
                  setTheme(theme === "dark" ? "light" : "dark")
                }
                className="flex h-8 w-8 items-center justify-center cursor-pointer dark:text-white"
              >
                <svg
                  viewBox="0 0 16 16"
                  className="hidden h-6 w-6 dark:block"
                >
                  <path
                    fill="#FFFFFF"
                    d="M4.50663 3.2267L3.30663 2.03337L2.36663 2.97337L3.55996 4.1667L4.50663 3.2267ZM2.66663 7.00003H0.666626V8.33337H2.66663V7.00003ZM8.66663 0.366699H7.33329V2.33337H8.66663V0.366699ZM13.6333 2.97337L12.6933 2.03337L11.5 3.2267L12.44 4.1667L13.6333 2.97337ZM11.4933 12.1067L12.6866 13.3067L13.6266 12.3667L12.4266 11.1734L11.4933 12.1067ZM13.3333 7.00003V8.33337H15.3333V7.00003H13.3333ZM7.99996 3.6667C5.79329 3.6667 3.99996 5.46003 3.99996 7.6667C3.99996 9.87337 5.79329 11.6667 7.99996 11.6667C10.2066 11.6667 12 9.87337 12 7.6667C12 5.46003 10.2066 3.6667 7.99996 3.6667ZM7.33329 14.9667H8.66663V13H7.33329V14.9667ZM2.36663 12.36L3.30663 13.3L4.49996 12.1L3.55996 11.16L2.36663 12.36Z"
                  />
                </svg>

                <svg
                  viewBox="0 0 23 23"
                  className="h-8 w-8 text-dark dark:hidden"
                >
                  <path d="M16.6111 15.855C17.591 15.1394 18.3151 14.1979 18.7723 13.1623C16.4824 13.4065 14.1342 12.4631 12.6795 10.4711C11.2248 8.47905 11.0409 5.95516 11.9705 3.84818C10.8449 3.9685 9.72768 4.37162 8.74781 5.08719C5.7759 7.25747 5.12529 11.4308 7.29558 14.4028C9.46586 17.3747 13.6392 18.0253 16.6111 15.855Z" />
                </svg>
              </button>

              <Link className="dark:text-white" to="#">Prtenership</Link>

              <Link
                to="/profile/notification"
                className="relative hover:text-red-400"
                onClick={handleNavbarClose}
                aria-label="Notifications"
              >
                <Icon icon="fa-solid:bell" width={24} />
                {notifications > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                    {notifications}
                  </span>
                )}
              </Link>

              {/* <Link
                to="/messenger"
                onClick={handleNavbarClose}
                className="relative text-white hover:text-red-400"
                aria-label="messenger"
              >
                  <img src="/images/commuinty/messenger.svg" alt="messenger olx logo" width={24}/>
                {messenger > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-xs text-white font-bold px-2 py-0.5 rounded-full">
                    {messenger}
                  </span>
                )}
              </Link> */}

              {/* Search button */}
              {/* <button
                onClick={() => setIsSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 hover:border-white/20 text-slate-300 hover:text-white transition-all group"
                aria-label="Open search"
              >
                <svg
                  className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.2}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
                <span className="hidden sm:inline text-xs">Search</span>
              </button> */}

            </div>

            <nav className="flex flex-col items-start p-4 space-y-4">
              {headerData.map((item: HeaderItem, index: number) => (
                <MobileHeaderLink
                  key={index}
                  item={item}
                  isOpen={openIndex === index}
                  onToggle={() => handleToggle(index)}
                  closeNavbar={handleNavbarClose}
                />
              ))}
            </nav>
          </div>
        </div>


        {/* Search Modal */}
        <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      </header>
    </>
  );
};

export default Header;
